import type { FC, RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import {
  calculateAvailableWidth,
  calculateVisibleCount,
} from "@locators/components/shared/components/StoreLocatorCardV2/components/StoreLocatorAvailableProducts/utils";

import styles from "./StoreLocatorProductAttributeValues.module.scss";

type Values<T> = Array<{ value: T }>;

type StoreLocatorProductAttributeValuesV2Props = {
  values: Values<string | number>;
  titleRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
};

export const StoreLocatorProductAttributeValues: FC<StoreLocatorProductAttributeValuesV2Props> = ({
  values,
  titleRef,
  containerRef,
}) => {
  const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(values.length);

  useEffect(() => {
    const update = () => {
      const [fullWidth, titleWidth] = [containerRef, titleRef].map((ref) => ref.current?.offsetWidth ?? 0);
      const availableWidth = calculateAvailableWidth(fullWidth, titleWidth);

      const count = calculateVisibleCount(values, itemRefs, availableWidth);

      setVisibleCount(count);
    };

    update();

    const observer = new ResizeObserver(update);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [values, titleRef, containerRef]);

  const displayed = values.slice(0, visibleCount);

  const hiddenCount = values.length - displayed.length;

  return (
    <div className={styles.productAttributeValues} data-testid="storeLocatorAttributeValues">
      <div className={styles.productAttributeValuesList}>
        {displayed.map(({ value }, idx) => (
          <span
            className={styles.productAttributesAdditional}
            key={value.toString()}
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
          >
            {value}
            {idx < displayed.length - 1 && " · "}
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <span className={styles.productAttributeValuesCount} data-testid="attributeValuesCount">
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};
